import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowRight, ArrowLeft, CheckCircle2, MessageCircle, ShoppingBag, Gift, Truck, Copy, Upload, X, UserPlus, ExternalLink, FileText, Tag, Percent, CreditCard, Building2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useUser } from "../contexts/UserContext";
import { api, BACKEND_URL, formatCLP, WHATSAPP_LINK } from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Carrito", "Pago", "Datos & Comprobante"];

const Cart = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { items, updateQty, removeItem, totalPrice, clear, totalCount } = useCart();
  const [step, setStep] = useState(0);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [done, setDone] = useState(false);
  const [settings, setSettings] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  const [uploadingComp, setUploadingComp] = useState(false);

  // Discount state
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, name, percentage}
  const [couponLoading, setCouponLoading] = useState(false);

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState("link"); // 'link' | 'transferencia'

  const [form, setForm] = useState({
    nombre: "", telefono: "", correo: "", direccion: "", rut: "", observaciones: "",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const giftThreshold = settings?.gift_threshold || 20000;
  const deliveryThreshold = settings?.delivery_threshold || 30000;
  const subtotal = totalPrice || 0;

  const globalPct = (settings?.global_discount_active ? Number(settings?.global_discount_percent || 0) : 0);
  const couponPct = coupon ? Number(coupon.percentage || 0) : 0;
  const totalPct = Math.min(100, globalPct + couponPct);

  const globalDiscount = useMemo(() => Math.round(subtotal * globalPct / 100), [subtotal, globalPct]);
  const couponDiscount = useMemo(() => Math.round(subtotal * couponPct / 100), [subtotal, couponPct]);
  const finalTotal = useMemo(() => Math.max(0, subtotal - globalDiscount - couponDiscount), [subtotal, globalDiscount, couponDiscount]);

  const giftPct = Math.min(100, (finalTotal / giftThreshold) * 100);
  const deliveryPctBar = Math.min(100, (finalTotal / deliveryThreshold) * 100);
  const giftReached = finalTotal >= giftThreshold;
  const deliveryReached = finalTotal >= deliveryThreshold;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const useMyData = () => {
    if (!user) return;
    setForm({
      nombre: user.nombre || "",
      telefono: user.telefono || "",
      correo: user.correo || "",
      direccion: user.direccion || "",
      rut: user.rut || "",
      observaciones: form.observaciones,
    });
    toast.success("Datos cargados ✓");
  };

  const applyCoupon = async () => {
    const code = (couponInput || "").trim().toUpperCase();
    if (!code) { toast.error("Ingresa un código"); return; }
    setCouponLoading(true);
    try {
      const res = await api.post("/discount-codes/validate", { code });
      if (res.data.valid) {
        setCoupon({ code: res.data.code, name: res.data.name, percentage: res.data.percentage });
        toast.success(`Cupón ${res.data.code} aplicado: −${res.data.percentage}%`);
      } else {
        const reasons = { no_existe: "Cupón no existe", inactivo: "Cupón inactivo", expirado: "Cupón vencido", vacio: "Código vacío" };
        toast.error(reasons[res.data.reason] || "Cupón inválido");
        setCoupon(null);
      }
    } catch (err) {
      toast.error("Error al validar cupón");
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
    toast.success("Cupón removido");
  };

  const onUploadComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingComp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload-public", fd);
      setComprobante({ path: res.data.storage_path, url: `${BACKEND_URL}${res.data.url}`, name: file.name });
      toast.success("Comprobante cargado ✓");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar comprobante");
    } finally { setUploadingComp(false); }
  };

  const submitOrder = async () => {
    if (!form.nombre || !form.telefono || !form.correo || !form.direccion || !form.rut) {
      toast.error("Por favor completa todos los campos requeridos"); return;
    }
    if (!comprobante) {
      toast.error("Por favor adjunta el comprobante de pago"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items,
        comprobante_path: comprobante.path,
        user_id: user?.id || null,
        payment_method: paymentMethod,
        discount_code: coupon?.code || null,
        discount_code_pct: couponPct,
        discount_global_pct: globalPct,
        subtotal: subtotal || null,
        total: finalTotal || null,
      };
      const res = await api.post("/orders", payload);
      setOrderId(res.data.id);
      setDone(true);
      clear();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar pedido");
    } finally { setSubmitting(false); }
  };

  const copyTotal = () => {
    if (finalTotal) {
      navigator.clipboard.writeText(String(Math.round(finalTotal)));
      toast.success(`Monto $${Math.round(finalTotal).toLocaleString("es-CL")} copiado`);
    }
  };

  const handleContinueToPay = () => {
    if (!user) {
      setShowRegisterModal(true);
    } else {
      setStep(1);
    }
  };

  if (done) {
    return (
      <div className="pt-12 pb-24 min-h-screen bg-cream flex items-center" data-testid="cart-success">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex h-20 w-20 items-center justify-center bg-yolk border-2 border-black mb-8">
            <CheckCircle2 className="h-10 w-10 text-black" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-6">
            ¡Recibimos tu pedido!
          </h1>
          <p className="text-gray-600 leading-relaxed mb-2">Pedido <span className="font-bold">#{orderId?.slice(0, 8).toUpperCase()}</span></p>
          <p className="text-gray-600 leading-relaxed mb-8">Te contactaremos por WhatsApp para confirmar y coordinar la entrega.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_LINK(`Hola, soy ${form.nombre}, acabo de enviar el pedido #${orderId?.slice(0,8).toUpperCase()}`)} target="_blank" rel="noreferrer" data-testid="success-wa" className="btn-chunky inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 text-sm">
              <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
            </a>
            <button onClick={() => navigate("/")} data-testid="success-home" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-6 py-4 text-sm">
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-8 pb-24 min-h-screen bg-cream">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 md:gap-6 mb-10" data-testid="cart-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2 md:gap-3">
                <div className={`h-8 w-8 flex items-center justify-center text-xs font-bold border-2 border-black ${i <= step ? "bg-mint-deep text-white" : "bg-white text-gray-400"}`}>
                  {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`text-xs uppercase tracking-[0.18em] font-bold hidden sm:inline ${i <= step ? "text-brand-ink" : "text-gray-400"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px w-6 md:w-16 ${i < step ? "bg-mint-deep" : "bg-gray-300"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Cart */}
        {step === 0 && (
          <div data-testid="cart-step-items">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-2">
              Tu <span className="font-accent italic text-mint-deep">carrito</span>
            </h1>
            <p className="text-gray-600 mb-8">{totalCount} {totalCount === 1 ? "producto" : "productos"} seleccionados</p>

            {items.length === 0 ? (
              <div className="bg-white border-2 border-black p-16 text-center">
                <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-6">Tu carrito está vacío</p>
                <button onClick={() => navigate("/3d")} data-testid="cart-empty-go" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-6 py-3 text-sm">
                  Explorar figuras 3D
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-white border-2 border-black">
                    {items.map((item) => (
                      <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex items-center gap-4 p-4 border-b-2 border-black last:border-b-0">
                        <div className="h-20 w-20 bg-mint-light overflow-hidden border-2 border-black shrink-0">
                          {item.image_url && <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-brand-ink text-sm truncate">{item.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">{formatCLP(item.price)}</p>
                        </div>
                        <div className="flex items-center border-2 border-black">
                          <button onClick={() => updateQty(item.product_id, item.quantity - 1)} data-testid={`cart-decrease-${item.product_id}`} className="h-9 w-9 flex items-center justify-center hover:bg-yolk-light"><Minus className="h-3 w-3" /></button>
                          <span className="w-9 text-center text-sm font-bold">{item.quantity}</span>
                          <button onClick={() => updateQty(item.product_id, item.quantity + 1)} data-testid={`cart-increase-${item.product_id}`} className="h-9 w-9 flex items-center justify-center hover:bg-yolk-light"><Plus className="h-3 w-3" /></button>
                        </div>
                        <button onClick={() => removeItem(item.product_id)} data-testid={`cart-remove-${item.product_id}`} className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>

                  {/* COUPON BOX */}
                  <div className="bg-white border-2 border-black p-5" data-testid="cart-coupon-box">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="h-4 w-4 text-yolk" />
                      <h3 className="font-heading font-bold text-brand-ink text-sm">¿Tienes un cupón?</h3>
                    </div>
                    {!coupon ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder="INGRESA CÓDIGO"
                          data-testid="coupon-input"
                          className="flex-1 border-2 border-black px-4 py-3 text-sm font-mono uppercase tracking-wider"
                          onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                        />
                        <button onClick={applyCoupon} disabled={couponLoading} data-testid="coupon-apply-btn" className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-5 py-3 text-sm">
                          {couponLoading ? "..." : "Aplicar"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2 bg-mint-light border-2 border-mint-deep p-3">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-mint-deep font-bold">{coupon.name}</p>
                          <p className="font-mono font-bold text-brand-ink text-sm">{coupon.code} · −{coupon.percentage}%</p>
                        </div>
                        <button onClick={removeCoupon} data-testid="coupon-remove-btn" className="p-2 hover:bg-red-50">
                          <X className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    )}
                    {settings?.global_discount_active && globalPct > 0 && (
                      <p className="text-[11px] text-mint-deep mt-3 inline-flex items-center gap-1">
                        <Percent className="h-3 w-3" /> Tienda con −{globalPct}% activo. Los cupones se SUMAN al descuento global.
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Thermometer */}
                  <div className="bg-white border-2 border-black p-5" data-testid="cart-thermometer">
                    <h3 className="font-heading font-bold text-brand-ink mb-4 text-sm">¡Sigue sumando! 🎁</h3>

                    <div className="mb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="h-4 w-4 text-yolk" />
                        <span className="text-xs font-bold text-brand-ink">Regalo sorpresa GRATIS</span>
                      </div>
                      <div className="h-3 bg-gray-100 border-2 border-black overflow-hidden">
                        <div className={`h-full transition-all duration-500 ${giftReached ? "bg-yolk" : "bg-yolk/70"}`} style={{ width: `${giftPct}%` }} />
                      </div>
                      <p className="text-[10px] mt-1 text-gray-600">
                        {giftReached ? "✅ ¡Lo conseguiste!" : `Faltan ${formatCLP(giftThreshold - finalTotal)}`}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="h-4 w-4 text-mint-deep" />
                        <span className="text-xs font-bold text-brand-ink">Delivery GRATIS en Copiapó</span>
                      </div>
                      <div className="h-3 bg-gray-100 border-2 border-black overflow-hidden">
                        <div className={`h-full transition-all duration-500 ${deliveryReached ? "bg-mint-deep" : "bg-mint"}`} style={{ width: `${deliveryPctBar}%` }} />
                      </div>
                      <p className="text-[10px] mt-1 text-gray-600">
                        {deliveryReached ? "✅ ¡Envío gratis!" : `Faltan ${formatCLP(deliveryThreshold - finalTotal)}`}
                      </p>
                    </div>
                  </div>

                  {/* TOTAL CARD */}
                  <div className="bg-mint-deep text-white border-2 border-black p-5">
                    <div className="space-y-1 text-sm mb-3 opacity-95">
                      <div className="flex justify-between"><span>Subtotal</span><span>{formatCLP(subtotal)}</span></div>
                      {globalPct > 0 && (
                        <div className="flex justify-between text-yolk-light"><span>Desc. tienda (−{globalPct}%)</span><span>−{formatCLP(globalDiscount)}</span></div>
                      )}
                      {couponPct > 0 && (
                        <div className="flex justify-between text-yolk-light"><span>Cupón (−{couponPct}%)</span><span>−{formatCLP(couponDiscount)}</span></div>
                      )}
                      {totalPct > 0 && (
                        <p className="text-[10px] uppercase tracking-wider opacity-70 pt-1">Descuento total: −{totalPct}%</p>
                      )}
                    </div>
                    <p className="text-xs uppercase tracking-[0.18em] opacity-80">Total a pagar</p>
                    <p className="font-heading text-3xl font-extrabold mt-1" data-testid="cart-total">{totalPrice !== null ? formatCLP(finalTotal) : "A cotizar"}</p>
                    <button onClick={handleContinueToPay} data-testid="cart-next-btn" className="btn-chunky w-full mt-4 inline-flex items-center justify-center gap-2 bg-yolk text-black py-3 text-sm">
                      Continuar al pago <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Payment Method + Instructions */}
        {step === 1 && (
          <div data-testid="cart-step-pay">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-2">
              Elige cómo <span className="font-accent italic text-mint-deep">pagar</span>
            </h1>
            <p className="text-gray-600 mb-6">
              Selecciona el método y sigue las instrucciones. Guarda el comprobante.
            </p>

            {/* PAYMENT METHOD TOGGLE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="payment-method-toggle">
              <button
                onClick={() => setPaymentMethod("link")}
                data-testid="pay-method-link"
                className={`btn-chunky text-left p-5 ${paymentMethod === "link" ? "bg-mint-deep text-white" : "bg-white text-black"}`}
              >
                <CreditCard className={`h-6 w-6 mb-2 ${paymentMethod === "link" ? "text-yolk" : "text-mint-deep"}`} />
                <p className="font-heading font-bold">Tarjeta / Link de pago</p>
                <p className="text-xs opacity-80 mt-1">Mercado Pago, Webpay, etc.</p>
              </button>
              <button
                onClick={() => setPaymentMethod("transferencia")}
                data-testid="pay-method-transfer"
                className={`btn-chunky text-left p-5 ${paymentMethod === "transferencia" ? "bg-mint-deep text-white" : "bg-white text-black"}`}
              >
                <Building2 className={`h-6 w-6 mb-2 ${paymentMethod === "transferencia" ? "text-yolk" : "text-mint-deep"}`} />
                <p className="font-heading font-bold">Transferencia bancaria</p>
                <p className="text-xs opacity-80 mt-1">Te mostramos los datos</p>
              </button>
            </div>

            {/* Total + copy */}
            <div className="bg-white border-2 border-black p-6 mb-6">
              <h3 className="font-heading font-bold mb-3">📋 Resumen de tu compra</h3>
              <div className="text-sm space-y-1 mb-3 max-h-40 overflow-y-auto">
                {items.map((it) => (
                  <div key={it.product_id} className="flex justify-between">
                    <span>{it.quantity}× {it.name}</span>
                    <span className="text-gray-500">{it.price ? formatCLP(it.price * it.quantity) : "Cotizar"}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-black/10 pt-3 space-y-1 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatCLP(subtotal)}</span></div>
                {globalPct > 0 && <div className="flex justify-between text-mint-deep"><span>Desc. tienda (−{globalPct}%)</span><span>−{formatCLP(globalDiscount)}</span></div>}
                {couponPct > 0 && <div className="flex justify-between text-mint-deep"><span>Cupón {coupon?.code} (−{couponPct}%)</span><span>−{formatCLP(couponDiscount)}</span></div>}
              </div>
              <div className="pt-3 mt-3 border-t-2 border-black flex justify-between items-center">
                <span className="text-xs uppercase tracking-wider text-gray-500">Total a pagar</span>
                <button onClick={copyTotal} data-testid="copy-total-btn" className="inline-flex items-center gap-2 font-heading text-2xl font-extrabold text-mint-deep hover:underline">
                  {totalPrice !== null ? formatCLP(finalTotal) : "—"}
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">💡 Tip: presiona el monto para copiarlo</p>
            </div>

            {/* Payment instructions based on method */}
            {paymentMethod === "link" ? (
              <div className="bg-mint-light border-2 border-black p-6 mb-6" data-testid="pay-instructions-link">
                <h3 className="font-heading font-bold mb-3">💳 Paga con tarjeta</h3>
                <ol className="space-y-2 text-sm mb-4">
                  <li><strong>1.</strong> Copia el monto exacto arriba.</li>
                  <li><strong>2.</strong> Haz clic en "Ir a pagar" para abrir el link de pago.</li>
                  <li><strong>3.</strong> Toma captura del comprobante de pago.</li>
                  <li><strong>4.</strong> Vuelve y sube el comprobante en el siguiente paso.</li>
                </ol>
                {settings?.payment_link ? (
                  <a href={settings.payment_link} target="_blank" rel="noreferrer" data-testid="payment-link-btn" className="btn-chunky inline-flex items-center justify-center gap-2 bg-yolk text-black px-6 py-3 text-sm">
                    <ExternalLink className="h-4 w-4" /> IR A PAGAR
                  </a>
                ) : (
                  <a href={WHATSAPP_LINK("Hola, quiero pagar mi pedido por link. ¿Pueden enviarme el link de pago?")} target="_blank" rel="noreferrer" data-testid="payment-wa-btn" className="btn-chunky inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm">
                    <MessageCircle className="h-4 w-4" /> SOLICITAR LINK POR WHATSAPP
                  </a>
                )}
              </div>
            ) : (
              <div className="bg-yolk-light border-2 border-black p-6 mb-6" data-testid="pay-instructions-transfer">
                <h3 className="font-heading font-bold mb-3">🏦 Paga por transferencia bancaria</h3>
                <ol className="space-y-2 text-sm mb-4">
                  <li><strong>1.</strong> Copia el monto exacto arriba.</li>
                  <li><strong>2.</strong> Usa los datos bancarios de abajo para hacer la transferencia.</li>
                  <li><strong>3.</strong> Guarda captura del comprobante.</li>
                  <li><strong>4.</strong> Vuelve y sube el comprobante en el siguiente paso.</li>
                </ol>
                {settings?.bank_info ? (
                  <div className="bg-white border-2 border-black p-4 text-sm whitespace-pre-wrap font-mono leading-relaxed" data-testid="bank-info-box">
                    {settings.bank_info}
                  </div>
                ) : (
                  <div className="bg-white border-2 border-black p-4 text-sm">
                    <p className="mb-3">Los datos bancarios no están configurados todavía. Por favor solicítalos por WhatsApp.</p>
                    <a href={WHATSAPP_LINK("Hola, quiero pagar por transferencia. ¿Pueden enviarme los datos bancarios?")} target="_blank" rel="noreferrer" className="btn-chunky inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 text-xs">
                      <MessageCircle className="h-3 w-3" /> Solicitar por WhatsApp
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(0)} data-testid="pay-back-btn" className="btn-chunky inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-sm">
                <ArrowLeft className="h-4 w-4" /> Volver
              </button>
              <button onClick={() => setStep(2)} data-testid="pay-continue-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm flex-1 justify-center">
                Ya pagué, continuar <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Form + Comprobante */}
        {step === 2 && (
          <div data-testid="cart-step-form">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-2">
              Tus <span className="font-accent italic text-mint-deep">datos</span>
            </h1>
            <p className="text-gray-600 mb-6">
              Adjunta el comprobante y completa tus datos de envío.
            </p>

            <div className="bg-yolk-light border-2 border-black p-4 mb-6 text-sm">
              <strong>Importante:</strong> recuerda haber realizado el pago por <strong>{paymentMethod === "transferencia" ? "transferencia bancaria" : "tarjeta / link de pago"}</strong> antes de continuar. Adjunta el comprobante (captura) y nos pondremos en contacto contigo.
            </div>

            <div className="bg-white border-2 border-black p-6 md:p-8 space-y-5">
              {user && (
                <button onClick={useMyData} data-testid="use-my-data-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint text-black px-4 py-2.5 text-xs">
                  <UserPlus className="h-3 w-3" /> USAR MIS DATOS
                </button>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre y apellido *" name="nombre" value={form.nombre} onChange={onChange} testid="order-nombre" />
                <Field label="RUT *" name="rut" value={form.rut} onChange={onChange} testid="order-rut" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Teléfono *" name="telefono" value={form.telefono} onChange={onChange} testid="order-telefono" />
                <Field label="Correo *" name="correo" type="email" value={form.correo} onChange={onChange} testid="order-correo" />
              </div>
              <Field label="Dirección completa *" name="direccion" value={form.direccion} onChange={onChange} testid="order-direccion" />
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Observación</label>
                <textarea name="observaciones" value={form.observaciones} onChange={onChange} rows={3} data-testid="order-observaciones" placeholder="Notas adicionales, fecha de entrega, preferencias..." className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Comprobante de pago *</label>
                <label className={`btn-chunky cursor-pointer inline-flex items-center gap-2 px-5 py-3 text-sm ${comprobante ? "bg-mint text-black" : "bg-yolk text-black"}`} data-testid="upload-comprobante-label">
                  <Upload className="h-4 w-4" />
                  {uploadingComp ? "Subiendo..." : comprobante ? "Cambiar comprobante" : "CARGAR COMPROBANTE"}
                  <input type="file" accept="image/*,.pdf" onChange={onUploadComprobante} className="hidden" data-testid="upload-comprobante-input" />
                </label>
                {comprobante && (
                  <div className="mt-3 flex items-center gap-3 p-3 bg-mint-light border-2 border-black">
                    <FileText className="h-4 w-4 text-mint-deep" />
                    <span className="text-sm flex-1 truncate">{comprobante.name}</span>
                    <button onClick={() => setComprobante(null)} className="text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button onClick={() => setStep(1)} data-testid="form-back-btn" className="btn-chunky inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-3.5 text-sm">
                  <ArrowLeft className="h-4 w-4" /> Volver
                </button>
                <button onClick={submitOrder} disabled={submitting} data-testid="form-submit-btn" className="btn-chunky flex-1 inline-flex items-center justify-center gap-2 bg-mint-deep text-white px-5 py-3.5 text-sm">
                  {submitting ? "Enviando..." : "ENVIAR PEDIDO"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: Register prompt */}
        {showRegisterModal && (
          <ModalShell onClose={() => setShowRegisterModal(false)} testid="register-prompt-modal">
            <div className="text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center bg-yolk border-2 border-black mb-5">
                <Gift className="h-8 w-8 text-black" />
              </div>
              <h2 className="font-heading text-2xl font-extrabold mb-3">¿Quieres ser parte del club ALA?</h2>
              <p className="text-gray-600 mb-6">
                Regístrate gratis para recibir promociones exclusivas, novedades y para hacer tu próxima compra
                aún más rápida.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => { setShowRegisterModal(false); navigate("/registro", { state: { redirect: "/carrito" } }); }} data-testid="register-yes-btn" className="btn-chunky flex-1 bg-yolk text-black py-3 text-sm">
                  SÍ, REGISTRARME
                </button>
                <button onClick={() => { setShowRegisterModal(false); setStep(1); }} data-testid="register-no-btn" className="btn-chunky flex-1 bg-white text-black py-3 text-sm">
                  No, continuar
                </button>
              </div>
            </div>
          </ModalShell>
        )}
      </div>
    </div>
  );
};

const ModalShell = ({ children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    <div className="bg-white border-2 border-black w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="p-6 md:p-8">{children}</div>
    </div>
  </div>
);

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} data-testid={testid} className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep" />
  </div>
);

export default Cart;
